import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner';
import { colors } from './theme';

const API_BASE = process.env.REACT_APP_API_URL;
const LEGACY_RMA_URL = 'https://northinvent.com/rma/';

const slowMessages = [
  'Hang tight, almost there…',
  'Looking up your device…',
  'Just a moment…',
];

export default function HelpdeskPage() {
  const navigate = useNavigate();
  const [tag, setTag] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [slowMessage, setSlowMessage] = useState('');
  const [notFoundTag, setNotFoundTag] = useState('');
  const [legacyOpen, setLegacyOpen] = useState(false);
  const cooldownRef = useRef(null);
  const slowTimerRef = useRef(null);
  const hintRef = useRef(null);
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);

  // Only offered when this exact tag came back as a genuine 404 — never for a
  // transient failure, and it hides again as soon as the tag is edited.
  const showLegacyHint = !loading && notFoundTag !== '' && tag.trim() === notFoundTag;

  const startCooldown = () => {
    setCooldown(10);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
  }, []);

  useEffect(() => {
    if (!legacyOpen) return undefined;
    const opener = hintRef.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (cancelRef.current) cancelRef.current.focus();
    const onKey = e => {
      if (e.key === 'Escape') { setLegacyOpen(false); return; }
      if (e.key !== 'Tab') return;
      const first = confirmRef.current;
      const last = cancelRef.current;
      if (!first || !last) return;
      const active = document.activeElement;
      // Focus can sit outside both controls (e.g. after clicking the dialog copy),
      // so pull it back in rather than only guarding the two boundaries.
      if (active !== first && active !== last) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [legacyOpen]);

  const handleSearch = async () => {
    const trimmed = tag.trim();
    if (!trimmed) { setMessage('Please enter your service tag.'); return; }
    if (cooldown > 0 || loading) return;

    setMessage('');
    setSlowMessage('');
    setNotFoundTag('');
    setLoading(true);
    startCooldown();

    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    slowTimerRef.current = setTimeout(() => {
      setSlowMessage(slowMessages[Math.floor(Math.random() * slowMessages.length)]);
    }, 3000);

    try {
      const res = await fetch(`${API_BASE}/device/service-tag/${encodeURIComponent(trimmed)}`);
      if (!res.ok) {
        let msg = 'Unable to search right now. Please try again later.';
        try {
          const data = await res.json();
          if (res.status === 404) {
            msg = 'No device found for this service tag. Double-check the characters and try once more.';
            setNotFoundTag(trimmed);
          }
          else if (res.status === 429) msg = data?.message || 'Please wait before trying again.';
          else if (data?.message) msg = data.message;
        } catch (_) {}
        setMessage(msg);
        return;
      }
      const payload = await res.json();
      const target = payload?.device ?? payload;
      if (target?._id) {
        navigate(`/device/${target._id}`);
      } else {
        setMessage('Device located but missing an identifier. Please contact support.');
      }
    } catch (err) {
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
      setSlowMessage('');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <section className="ni-panel ni-panel--center ni-hero">
        <style>{`
          @keyframes legacyHintIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
          @keyframes legacyFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes legacySlideUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
          .ni-status-stack { display: grid; gap: 0.5rem; justify-items: center; width: 100%; }
          .ni-status-stack:empty { display: none; }
          .ni-legacy-link:hover { color: ${colors.primary}; text-decoration-color: ${colors.primary}; }
          .ni-legacy-link:focus-visible { color: ${colors.primary}; outline: 2px solid ${colors.primary}; outline-offset: 2px; border-radius: 4px; }
          @media (prefers-reduced-motion: reduce) { .ni-legacy-anim { animation: none !important; } }
        `}</style>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="ni-button ni-button--ghost"
          style={{ alignSelf: 'flex-start', fontSize: '0.8rem', padding: '0.2rem 0.6rem', marginBottom: '0.5rem' }}
        >
          ← Main
        </button>
        <span className="ni-eyebrow">Asset Lookup</span>
        <h2 className="ni-hero-title">Enter your service tag to view your product</h2>
        <p className="ni-hero-copy">Enter the exact service tag number to open your device overview.</p>
        <div style={heroForm}>
          <input
            value={tag}
            onChange={e => { setTag(e.target.value); if (message) setMessage(''); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
            className="ni-input"
            style={heroInput}
            placeholder="Service Tag Number"
            aria-label="Service Tag Number"
          />
          <button
            onClick={handleSearch}
            disabled={loading || cooldown > 0}
            className="ni-button ni-button--primary"
            style={{ ...heroButton, opacity: loading || cooldown > 0 ? 0.7 : 1, cursor: loading || cooldown > 0 ? 'not-allowed' : 'pointer' }}
          >
            {cooldown > 0 ? `Please wait (${cooldown}s)` : 'Find my product'}
          </button>
        </div>
        <p style={{ ...heroSlowMessage, opacity: slowMessage ? 1 : 0 }}>{slowMessage || '\u00A0'}</p>
        {loading && <div style={{ marginTop: '1rem' }}><LoadingSpinner size={40} /></div>}
        <div className="ni-status-stack" role="status">
          {message && <p style={heroMessage}>{message}</p>}
          {showLegacyHint && (
            <p className="ni-legacy-anim" style={legacyHintText}>
              If you're certain this product isn't in our system, try{' '}
              <button
                type="button"
                ref={hintRef}
                className="ni-legacy-link"
                aria-haspopup="dialog"
                aria-label="Try the legacy portal"
                onClick={() => setLegacyOpen(true)}
                style={legacyHintLink}
              >the legacy portal</button>.
            </p>
          )}
        </div>
        <p className="ni-note" style={{ margin: 0 }}>Early access</p>
        {legacyOpen && (
          <div
            className="ni-legacy-anim"
            style={legacyOverlay}
            onClick={e => { if (e.target === e.currentTarget) setLegacyOpen(false); }}
          >
            <div
              className="ni-legacy-anim"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ni-legacy-title"
              aria-describedby="ni-legacy-body"
              style={legacyCard}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <h2 id="ni-legacy-title" style={legacyTitle}>Continue to the legacy portal?</h2>
                <p id="ni-legacy-body" style={legacyBody}>
                  This opens our older RMA portal at northinvent.com in a new tab. If there's any
                  chance the service tag was mistyped, it's worth checking the label once more first.
                </p>
              </div>
              <div style={legacyActions}>
                <a
                  ref={confirmRef}
                  href={LEGACY_RMA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ni-button ni-button--primary"
                  style={legacyConfirm}
                  aria-label="Open legacy portal (opens in a new tab)"
                  onClick={() => setLegacyOpen(false)}
                >
                  Open legacy portal
                </a>
                <button
                  type="button"
                  ref={cancelRef}
                  className="ni-button ni-button--ghost"
                  style={legacyCancel}
                  onClick={() => setLegacyOpen(false)}
                >
                  Stay here
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const heroForm = { display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', alignItems: 'center' };
const heroInput = { maxWidth: '420px' };
const heroButton = { width: '100%', maxWidth: '420px', justifyContent: 'center' };
const heroSlowMessage = { color: colors.textMuted, fontSize: '0.875rem', transition: 'opacity 0.3s' };
const heroMessage = { color: colors.textStrong, fontWeight: '500' };
const legacyHintText = { margin: 0, color: colors.textMuted, fontSize: '0.8rem', lineHeight: 1.5, maxWidth: '420px', animation: 'legacyHintIn 260ms ease 700ms both' };
const legacyHintLink = { font: 'inherit', color: colors.textBody, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px', padding: '0.35rem 0.15rem', margin: '-0.35rem -0.15rem' };
const legacyOverlay = { position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', overflowY: 'auto', animation: 'legacyFadeIn 0.2s ease' };
const legacyCard = { background: '#111b16', border: '1px solid rgba(70,187,137,0.22)', borderRadius: '16px', padding: '2.5rem 2rem', maxWidth: '420px', width: '100%', display: 'flex', flexDirection: 'column', gap: '1.25rem', alignItems: 'center', textAlign: 'center', boxShadow: '0 28px 72px rgba(0,0,0,0.65)', animation: 'legacySlideUp 0.28s ease' };
const legacyTitle = { margin: 0, color: colors.textStrong, fontSize: '1.25rem', fontWeight: 700 };
const legacyBody = { margin: 0, color: colors.textMuted, fontSize: '0.9rem', lineHeight: 1.65 };
const legacyActions = { display: 'flex', flexDirection: 'column', gap: '0.6rem', width: '100%' };
const legacyConfirm = { width: '100%', fontSize: '1rem', padding: '0.7rem 1.5rem', textDecoration: 'none', justifyContent: 'center' };
const legacyCancel = { width: '100%', fontSize: '0.88rem', padding: '0.6rem 1.5rem', justifyContent: 'center' };
